import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import SearchBar from '@/components/base/SearchBar';
import TvCard from '@/components/base/TvCard';
import NewsRow from '@/components/base/NewsRow';
import ActorCard from '@/components/base/ActorCard';
import EmptyState from '@/components/base/EmptyState';
import { tvSeriesList } from '@/mocks/tvSeries';
import { newsList } from '@/mocks/newsList';
import { actorList } from '@/mocks/actors';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';

type TabKey = 'all' | 'tv' | 'news' | 'actors';

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: 'all', label: '全部', icon: 'ri-apps-2-line' },
  { key: 'tv', label: '电视剧', icon: 'ri-tv-2-line' },
  { key: 'news', label: '影视资讯', icon: 'ri-newspaper-line' },
  { key: 'actors', label: '演员动态', icon: 'ri-user-star-line' },
];

export default function SearchPage() {
  const [params] = useSearchParams();
  const keyword = (params.get('q') || '').trim();
  const [tab, setTab] = useState<TabKey>('all');
  const { isFavorite, toggleFavorite } = useFavorites();

  usePageMeta(
    keyword ? `“${keyword}” 的搜索结果 - 漂亮的姐姐` : '全站搜索 - 漂亮的姐姐',
    '在漂亮的姐姐搜索电视剧、影视资讯与演员动态，支持剧名、演员名与关键词的模糊匹配。',
  );

  const lower = keyword.toLowerCase();

  const tvResults = useMemo(
    () =>
      !lower
        ? []
        : tvSeriesList.filter(
            (item) =>
              item.title.toLowerCase().includes(lower) ||
              item.cast.some((name) => name.toLowerCase().includes(lower)) ||
              item.tags.some((tag) => tag.toLowerCase().includes(lower)),
          ),
    [lower],
  );

  const newsResults = useMemo(
    () =>
      !lower
        ? []
        : newsList.filter(
            (item) =>
              item.title.toLowerCase().includes(lower) ||
              item.summary.toLowerCase().includes(lower) ||
              item.tags.some((tag) => tag.toLowerCase().includes(lower)),
          ),
    [lower],
  );

  const actorResults = useMemo(
    () =>
      !lower
        ? []
        : actorList.filter(
            (actor) => actor.name.toLowerCase().includes(lower) || actor.summary.toLowerCase().includes(lower),
          ),
    [lower],
  );

  const total = tvResults.length + newsResults.length + actorResults.length;

  const countByTab = (key: TabKey) => {
    if (key === 'tv') return tvResults.length;
    if (key === 'news') return newsResults.length;
    if (key === 'actors') return actorResults.length;
    return total;
  };

  const showTv = tab === 'all' || tab === 'tv';
  const showNews = tab === 'all' || tab === 'news';
  const showActors = tab === 'all' || tab === 'actors';

  const hotLinks = [...tvSeriesList].sort((a, b) => b.popularity - a.popularity).slice(0, 4);

  return (
    <div className="w-full max-w-page mx-auto px-4 md:px-6 py-6">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />

      <header className="mt-5">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">全站搜索</h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600">输入剧名、演员名或资讯关键词，快速查找你想了解的内容。</p>
      </header>

      <div className="mt-5 max-w-2xl">
        <SearchBar size="lg" defaultValue={keyword} placeholder="搜索剧名、演员或资讯关键词…" />
      </div>

      {keyword ? (
        <>
          <p className="mt-5 text-sm text-foreground-600">
            关键词 <strong className="text-primary-700">“{keyword}”</strong> 共找到 <strong className="text-accent-600">{total}</strong> 条结果
            <span className="ml-2 text-xs text-foreground-400">（演示数据匹配结果）</span>
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {tabs.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                  tab === item.key
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className={item.icon}></i>
                {item.label}
                <span className={`text-xs ${tab === item.key ? 'text-background-100' : 'text-foreground-500'}`}>({countByTab(item.key)})</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <p className="mt-5 text-sm text-foreground-600">还没有输入关键词，试试搜索上面的热门剧集或输入你感兴趣的内容。</p>
      )}

      {keyword && total === 0 ? (
        <div className="mt-6">
          <EmptyState
            title="没有找到匹配的内容"
            description={`没有与“${keyword}”相关的剧集、资讯或演员。换个关键词试试，或浏览下方热门推荐。`}
            icon="ri-search-eye-line"
          />
        </div>
      ) : null}

      <div className="mt-8 flex flex-col gap-10">
        {showTv && tvResults.length > 0 ? (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">电视剧（{tvResults.length}）</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {tvResults.map((series) => (
                <TvCard key={series.id} series={series} favorite={isFavorite(series.id)} onToggleFavorite={toggleFavorite} />
              ))}
            </div>
          </section>
        ) : null}

        {showNews && newsResults.length > 0 ? (
          <section>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">影视资讯（{newsResults.length}）</h2>
            </div>
            <div className="bg-background-50 border border-background-200 rounded-lg px-4 md:px-5">
              {newsResults.map((item) => (
                <NewsRow key={item.id} item={item} />
              ))}
            </div>
          </section>
        ) : null}

        {showActors && actorResults.length > 0 ? (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-1.5 h-6 rounded-full bg-secondary-500"></span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">演员动态（{actorResults.length}）</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-5">
              {actorResults.map((actor) => (
                <ActorCard key={actor.id} actor={actor} />
              ))}
            </div>
          </section>
        ) : null}

        {(total === 0 || !keyword) && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">热门内容推荐</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {hotLinks.map((series) => (
                <TvCard key={series.id} series={series} favorite={isFavorite(series.id)} onToggleFavorite={toggleFavorite} />
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/category/tv-series" className="inline-flex items-center gap-1 text-sm font-label text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-tv-2-line"></i>
                浏览电视剧分类
              </Link>
              <Link to="/category/news" className="inline-flex items-center gap-1 text-sm font-label text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-newspaper-line"></i>
                浏览影视资讯
              </Link>
              <Link to="/category/actors" className="inline-flex items-center gap-1 text-sm font-label text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-user-star-line"></i>
                浏览演员动态
              </Link>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}